/**
 * NA-283 delete-eligibility presentation helpers.
 *
 * Eligibility itself is server-computed (see the API's deleteEligibility
 * service) — these only turn it into UI state. Never reconstruct eligibility
 * from workflowState / assignedAccountIds / courseUsageCount: those are scoped
 * differently from the delete guard and would offer Delete on rows that 409.
 */

import type { DeleteEligibility } from '@course-authoring/shared-types';

/** Missing eligibility reads as blocked — a delete offered in error is worse than one withheld. */
export function canDelete(eligibility: DeleteEligibility | undefined): boolean {
  return eligibility?.deletable === true;
}

const pluralize = (count: number, noun: string): string =>
  `${count} ${count === 1 ? noun : `${noun}s`}`;

/**
 * Accessible reason for a disabled Delete control, built from the gating
 * predicate's own count so the copy can never contradict a refusal.
 * Returns null when the item is deletable.
 */
export function describeDeleteBlock(eligibility: DeleteEligibility | undefined): string | null {
  if (canDelete(eligibility)) return null;
  const count = eligibility?.blockingCount ?? 0;
  if (eligibility?.reason === 'usedInCourses') return `Used in ${pluralize(count, 'course')}`;
  if (eligibility?.reason === 'assignedToAccounts') return `Assigned to ${pluralize(count, 'account')}`;
  return 'This item cannot be deleted';
}
